import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface Order {
  id: string;
  product_id: string;
  product_name: string;
  amount: number;
  payment_status: string;
  order_status: string;
  created_at: string;
  order_type: string;
}

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [orderTypeFilter, setOrderTypeFilter] = useState<string>('all');

  useEffect(() => {
    // 检查用户是否已登录
    const userStr = localStorage.getItem('user');
    if (!userStr) {
      navigate('/login', { state: { from: '/profile' } });
      return;
    }

    try {
      const userData = JSON.parse(userStr);
      setUser(userData);
      fetchUserOrders(userData.email);
    } catch (e) {
      console.error('Error parsing user data:', e);
      navigate('/login', { state: { from: '/profile' } });
    }
  }, [navigate]);

  const fetchUserOrders = async (email: string) => {
    try {
      setLoading(true);
      console.log('Fetching orders for email:', email);
      // 这里需要后端提供获取用户订单的API
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/auth/user/orders?email=${encodeURIComponent(email)}`);
      console.log('API response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('API response data:', data);
        setOrders(data.orders || []);
      } else {
        console.error('API response not ok:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('Error response:', errorText);
      }
    } catch (error) {
      console.error('Error fetching user orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    navigate('/');
  };

  const handleDownload = async (productId: string, orderId?: string) => {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        alert('请先登录');
        return;
      }

      const userData = JSON.parse(userStr);
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      
      let downloadUrl = `${API_BASE}/api/products/${productId}/download`;
      const params = new URLSearchParams();
      params.append('email', userData.email);
      
      if (orderId) {
        params.append('order_id', orderId);
      }
      
      downloadUrl += `?${params.toString()}`;
      
      const response = await fetch(downloadUrl);
      if (!response.ok) {
        throw new Error('下载失败');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `训练计划.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      alert('下载开始！');
    } catch (error) {
      console.error('Download error:', error);
      alert('下载失败，请稍后重试');
    }
  };

  const filteredOrders = orders.filter(order => {
    if (orderTypeFilter === 'paid') return order.amount > 0;
    if (orderTypeFilter === 'free') return order.amount === 0;
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">加载中...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 头部 */}
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-lg">
                  {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                </span>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{user.name}</h1>
                <p className="text-gray-600">{user.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-colors duration-200"
            >
              退出登录
            </button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* 统计卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-full">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">总订单数</p>
                <p className="text-2xl font-bold text-gray-900">{orders.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-full">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">付费订单</p>
                <p className="text-2xl font-bold text-gray-900">
                  {orders.filter(order => order.amount > 0).length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-3 bg-orange-100 rounded-full">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">免费下载</p>
                <p className="text-2xl font-bold text-gray-900">
                  {orders.filter(order => order.amount === 0).length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 订单列表 */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">我的订单</h2>
              <select
                value={orderTypeFilter}
                onChange={(e) => setOrderTypeFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="all">全部订单</option>
                <option value="paid">付费订单</option>
                <option value="free">免费订单</option>
              </select>
            </div>
          </div>

          {filteredOrders.length === 0 ? (
            <div className="p-8 text-center">
              <div className="text-gray-400 mb-4">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
              </div>
              <p className="text-gray-600 mb-4">还没有订单记录</p>
              <button
                onClick={() => navigate('/products')}
                className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-lg transition-colors duration-200"
              >
                浏览商品
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredOrders.map((order) => (
                <div key={order.id} className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{order.product_name}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          order.amount === 0 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {order.amount === 0 ? '免费' : '付费'}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <p>订单号: {order.id}</p>
                        <p>金额: ¥{order.amount.toFixed(2)}</p>
                        <p>状态: {order.payment_status === 'succeeded' ? '已支付' : '待支付'}</p>
                        <p>创建时间: {new Date(order.created_at).toLocaleString('zh-CN')}</p>
                      </div>
                    </div>
                                         <div className="flex items-center space-x-3">
                       {order.payment_status === 'succeeded' && (
                         <button
                           onClick={() => handleDownload(order.product_id, order.id)}
                           className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg transition-colors duration-200"
                         >
                           下载
                         </button>
                       )}
                       <button
                         onClick={() => navigate(`/product/${order.product_id}`)}
                         className="text-orange-500 hover:text-orange-600 px-4 py-2 rounded-lg transition-colors duration-200"
                       >
                         查看详情
                       </button>
                     </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile; 